import { useQueryClient, type InfiniteData } from '@tanstack/vue-query'
import { onBeforeMount, onBeforeUnmount } from 'vue'
import { wsClient } from '#shared/api/ws-client'
import type { Chat } from '../model/chat'
import type { ChatEvent } from '../model/chat-event'
import type { GetChatsResponse } from '../model/get-chats-response'
import { getChatQueryKey } from './chat-query'
import { PERSONAL_CHATS_QUERY_KEY } from './personal-chats-query'
import { PUBLIC_CHATS_QUERY_KEY } from './public-chats-query'

export function useDynamicChatUpdates() {
  const queryClient = useQueryClient()

  const addChat = (chat: Chat) => {
    queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
      if (!queryData)
        queryData = {
          status: 'ok',
          hasNextPage: true,
          endCursor: null,
          data: [],
        }

      queryData.data = [chat, ...queryData.data]

      return queryData
    })

    queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
      if (!queryData) return

      const newPageParams = [...queryData.pageParams, null]
      const newPages = [...queryData.pages, { status: 'ok', hasNextPage: true, endCursor: null, data: [chat] }]

      return {
        pageParams: newPageParams,
        pages: newPages,
      }
    })
  }

  const deleteChat = (chat: Chat) => {
    queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
      if (!queryData) return

      const newData = queryData.data.filter(fetchedChat => fetchedChat.id !== chat.id)

      return {
        ...queryData,
        data: newData,
      }
    })

    queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
      if (!queryData) return

      const newPages = queryData.pages.map(page => ({
        ...page,
        data: page.data.filter(fetchedChat => fetchedChat.id !== chat.id),
      }))

      return {
        ...queryData,
        pages: newPages,
      }
    })

    queryClient.invalidateQueries({ queryKey: getChatQueryKey(chat.id) })
  }

  const onChatEvent = (chatEvent: ChatEvent) => {
    if (chatEvent.operation === 'create') {
      addChat(chatEvent.chat)
    }

    if (chatEvent.operation === 'delete') {
      deleteChat(chatEvent.chat)
    }
  }

  onBeforeMount(() => {
    wsClient.on('chat', onChatEvent)
  })

  onBeforeUnmount(() => {
    wsClient.off('chat', onChatEvent)
  })
}
